"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReportsResource = exports.KitchenResource = exports.PaymentsResource = exports.OrdersResource = void 0;
class OrdersResource {
    http;
    constructor(http) {
        this.http = http;
    }
    list(query) {
        return this.http.getPaginated('orders', { query });
    }
    /** Orders per status under the same filters as `list` (status ignored). */
    counts(query) {
        return this.http.get('orders/counts', { query });
    }
    get(id) {
        return this.http.get(`orders/${id}`);
    }
    /**
     * Prices a cart without persisting anything, so the customer sees the exact
     * total — including promotions and delivery — before confirming.
     */
    preview(payload) {
        return this.http.post('orders/preview', payload);
    }
    /**
     * Creates an order.
     *
     * When `clientUuid` is set it is also sent as the idempotency key, so a POS
     * replaying a queued offline order gets back the original instead of a
     * duplicate (TZ §18).
     */
    create(payload) {
        return this.http.post('orders', payload, {
            idempotencyKey: payload.clientUuid,
        });
    }
    updateStatus(id, payload) {
        return this.http.patch(`orders/${id}/status`, payload);
    }
    cancel(id, cancelReason) {
        return this.http.patch(`orders/${id}/status`, {
            status: 'CANCELLED',
            cancelReason,
        });
    }
    assignCourier(id, payload) {
        return this.http.post(`orders/${id}/courier`, payload);
    }
    /** Full timeline of the order (TZ §13). */
    timeline(id) {
        return this.http.get(`orders/${id}/timeline`);
    }
    /** Public tracking for the customer, keyed by the order's display number. */
    track(displayNumber, phone) {
        return this.http.get('orders/track', {
            query: { number: displayNumber, phone },
            skipAuth: true,
        });
    }
}
exports.OrdersResource = OrdersResource;
class PaymentsResource {
    http;
    constructor(http) {
        this.http = http;
    }
    create(payload) {
        return this.http.post('payments', payload);
    }
    /** Several tenders settling one order at once (TZ §17). */
    createMixed(payload) {
        return this.http.post('payments/mixed', payload);
    }
    refund(payload) {
        return this.http.post('payments/refund', payload);
    }
    listForOrder(orderId) {
        return this.http.get(`payments/order/${orderId}`);
    }
    /* --------------------------- Online (Subo) ------------------------ */
    /**
     * Whether online payment is configured.
     *
     * Reports only state — the API key is write-only, because Subo shows a
     * merchant key once and a leak from here would be unrecoverable.
     */
    suboConfig() {
        return this.http.get('payments/subo/config');
    }
    saveSuboConfig(payload) {
        return this.http.post('payments/subo/config', payload);
    }
    /**
     * Creates or resumes a Subo checkout for an order.
     *
     * Resuming is the point: a customer who closed the tab is sent back to the
     * same session rather than given a second live charge for one order.
     *
     * Omitting `provider` makes Subo use the first provider configured in the
     * merchant's Subo panel (docs.subo.uz). Naming `ATMOS` forces the card
     * path, which is the only one that saves a card for next time.
     */
    startCheckout(payload) {
        return this.http.post('payments/subo/checkout', payload);
    }
    /** Online attempts for an order — poll this after the customer returns. */
    listCheckouts(orderId) {
        return this.http.get(`payments/subo/order/${orderId}`);
    }
}
exports.PaymentsResource = PaymentsResource;
class KitchenResource {
    http;
    constructor(http) {
        this.http = http;
    }
    /** Open tickets for one branch, optionally narrowed to a station (TZ §22). */
    tickets(branchId, stationId) {
        return this.http.get('kitchen/tickets', {
            query: { branchId, stationId },
        });
    }
    start(ticketId) {
        return this.http.post(`kitchen/tickets/${ticketId}/start`);
    }
    ready(ticketId) {
        return this.http.post(`kitchen/tickets/${ticketId}/ready`);
    }
}
exports.KitchenResource = KitchenResource;
class ReportsResource {
    http;
    constructor(http) {
        this.http = http;
    }
    dashboard(query) {
        return this.http.get('reports/dashboard', { query });
    }
    /** Revenue by type, source, tender and time. */
    sales(query) {
        return this.http.get('reports/sales', { query });
    }
    /** Ranked by revenue, with an ABC split for kitchen planning. */
    products(query) {
        return this.http.get('reports/products', { query });
    }
    /** Every branch side by side, best first, with its daily target. */
    branches(query) {
        return this.http.get('reports/branches', { query });
    }
    /** Uses, discount and revenue per promotion, promo code and branch. */
    promotions(query) {
        return this.http.get('reports/promotions', { query });
    }
    /** Shift totals and every closing discrepancy. Needs `finance.view`. */
    finance(query) {
        return this.http.get('reports/finance', { query });
    }
}
exports.ReportsResource = ReportsResource;
//# sourceMappingURL=orders.js.map